import { Component, inject, Input, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { UploadFileDialogComponent } from '../../dialogs/upload-file-dialog/upload-file-dialog.component';
import { SubstanceTypeEnum } from 'app/enums/substance-type-enum';
import { FormService } from 'app/services/form.service';
import { DocumentConstants, DocumentExtensionHandling, FormRequiredDocumentsMapping } from 'app/constants/document.constants';
import { AttachmentModel } from 'app/models/attachment.model';

@Component({
  selector: 'app-structural-formula',
  templateUrl: './structural-formula.component.html',
  styleUrls: ['./structural-formula.component.scss']
})
export class StructuralFormulaComponent implements OnInit {
  @Input({required: true}) substanceType!: SubstanceTypeEnum.SingleEntity | SubstanceTypeEnum.Salt;
  @Input() shouldShowFullTitle!: boolean; // Only if single entity/salt shouldn't be displayed on their respective pages.
  private allowedFileTypes = [ ...DocumentExtensionHandling.CHEMDRAW_FILE_EXTENSIONS, ...DocumentExtensionHandling.IMAGE_FILE_EXTENSIONS ];
  private acceptedFileTypes = [ ...DocumentExtensionHandling.CHEMDRAW_ACCEPT_TYPE, ...DocumentExtensionHandling.IMAGE_ACCEPT_TYPE ];
  fileUploaded = false;
  private documentRequired: boolean = false; 
  readonly dialog = inject(MatDialog);
    public openDialog(): void {
      const inputType = this.getInputTypeDocumentConstant();
      // Shouldn't happen, but to prevent errors.
      if(!inputType) {
        return;
      }

      const dialogRef = this.dialog.open(UploadFileDialogComponent, {
          data: { 
            fileName: inputType,
            allowedFileTypes: this.allowedFileTypes,
            fileAcceptTypes: this.acceptedFileTypes
          }
      });
      dialogRef.afterClosed().subscribe(result => {
         if (result) {
          this.fileUploaded = true;
        }
      });
  
    }

    constructor(protected formService: FormService) {}
 
    ngOnInit(): void {
      this.determineDocumentRequired();
    }
    
    private getInputTypeDocumentConstant() {
      if(this.isSingleEntityStructuralFormulaInputType) {
        return DocumentConstants.STRUCTURAL_FORMULA_SINGLE_ENTITY;
      }

      if(this.isSaltStructuralFormulaInputType) {
        return DocumentConstants.STRUCTURAL_FORMULA_FOR_SALT;
      }
    
      return DocumentConstants.STRUCTURAL_FORMULA_FOR_NONE;
    }

    get isSingleEntityStructuralFormulaInputType() {
      return this.substanceType === SubstanceTypeEnum.SingleEntity;
    }
  
    get isSaltStructuralFormulaInputType() {
      return this.substanceType === SubstanceTypeEnum.Salt;
    }

    get isFormAType(): boolean {
      return 'FormA' === this.formType;
    }

    get isFormBType(): boolean {
      return 'FormB' === this.formType;
    }

    get isFormCType(): boolean {
      return 'FormC' === this.formType;
    }

    get isFormDType(): boolean {
      return 'FormD' === this.formType;
    }

    get isFormEType(): boolean {
      return 'FormE' === this.formType;
    }

    get isFormFType(): boolean {
      return 'FormF' === this.formType;
    }
  
  get formFieldTitle() {
    var title = 'Upload structural formula';

    if(this.isSingleEntityStructuralFormulaInputType && this.shouldShowFullTitle) {
      title += ' for single entity'; 
    }
    
    if(this.isSaltStructuralFormulaInputType && this.shouldShowFullTitle) {
      title += ' for salt form'; 
    }

    return title;
  }

  get displayText() {
    if(this.isFormAType) {
      return 'Editable ChemDraw File showing the structure(s) of the parent substance and its salt or ester'; 
    }

    if(this.isSingleEntityStructuralFormulaInputType && this.isFormBType) {
      return 'Editable ChemDraw File showing the structure of the parent substance'; 
    }

    if(this.isSingleEntityStructuralFormulaInputType && this.isFormCType) {
      return 'Editable ChemDraw File showing the structure of the salt or ester'; 
    }

    if(this.isSingleEntityStructuralFormulaInputType && this.isFormDType) {
      return 'Editable ChemDraw File showing the structure(s) of the substance for which a revision is being requested'; 
    }

    if(this.isSingleEntityStructuralFormulaInputType && this.isFormEType) {
      return 'Editable ChemDraw File showing the structure(s) of the substance'; 
    }

    if(this.isSingleEntityStructuralFormulaInputType && this.isFormFType) {
      return 'Editable ChemDraw File showing the structure of the substance'; 
    }
    
    if(this.isSaltStructuralFormulaInputType && !this.isFormAType) {
      return 'Editable ChemDraw or Image File showing the structure of the salt or ester (Provide stereochemistry)'; 
    }

    return '';
  }

  get secondaryText() {
    if(this.isSingleEntityStructuralFormulaInputType || this.isFormAType) {
      return `Please upload an editable ChemDraw file, if unavailable, please upload an Image file. (${this.imageExtensionsAsStr()})`;
    }
    return '';
  }

  get hasStructuralFormula(): boolean {
    const inputType = this.getInputTypeDocumentConstant();
    
    if(inputType) {
      return this.formService.doesCurrentFormHaveAttachmentForSection(inputType);
    }

    return false; 
  }

  get tertiaryText() {
    if(this.isSingleEntityStructuralFormulaInputType && this.isFormEType) {
      return `<ul>\
          <li>Structural formulas and stereochemistry, if known, must be supplied for polymer and each monomer.</li>\
          <li>If this is a submission for a hybrid lens material, please supply information for both the soft skirt and hard center.</li>\
        </ul>`;
    }

    return '';
  }

  private imageExtensionsAsStr(): string {
    return DocumentExtensionHandling.fileTypeArrayAsString(DocumentExtensionHandling.IMAGE_FILE_EXTENSIONS);
  }

  get attachments(): AttachmentModel[] {
    return this.formService.allAttachmentsForSection(this.getInputTypeDocumentConstant());
  }

  get formType(): string {
    return this.formService.getCurrentUsanFormType();
  }

  get isDocumentRequired(): boolean {
    return this.documentRequired;
  }
  
  get showRequiredError(): boolean {
    return this.documentRequired && this.formService.isShowingErrorSections && !this.hasStructuralFormula;
  }

  private determineDocumentRequired() {
    this.documentRequired = FormRequiredDocumentsMapping.isDocumentRequiredForForm(this.formService.getCurrentUsanFormType(), this.getInputTypeDocumentConstant());
  }
}
